// App.jsx — Root component with routing and global state

import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { usePublicContent, useDraftContent, useTheme, useLang, useAuth } from "./hooks/useStore";
import { TRANSLATIONS } from "./data/translations";
import HomePage from "./pages/HomePage";
import AdminPage from "./pages/AdminPage";
import NotFoundPage from "./pages/NotFoundPage";
import PageSkeleton from "./components/PageSkeleton";

export default function App() {
  const { dark, setDark } = useTheme();
  const { lang, setLang } = useLang();
  const { isAdmin, login, logout } = useAuth();
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;
  const { content } = usePublicContent();

  // Apply dark background to <html>
  if (typeof document !== "undefined") {
    document.documentElement.style.backgroundColor = dark ? "#0F172A" : "#ffffff";
  }

  // Dynamic favicon from logo
  useEffect(() => {
    const faviconUrl = content?.logo?.imageDarkUrl || content?.logo?.imageUrl;
    if (faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }
  }, [content?.logo?.imageDarkUrl, content?.logo?.imageUrl]);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute
              dark={dark} setDark={setDark}
              lang={lang} setLang={setLang}
              t={t} isAdmin={isAdmin}
            />
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute
              dark={dark} setDark={setDark}
              isAdmin={isAdmin} login={login} logout={logout}
            />
          }
        />
        <Route path="*" element={<NotFoundPage dark={dark} />} />
      </Routes>
    </BrowserRouter>
  );
}

// ── PublicRoute ───────────────────────────────────────────────────────────────
function PublicRoute({ dark, setDark, lang, setLang, t, isAdmin }) {
  const isDraftPreview = typeof window !== "undefined" && window.location.search.includes("preview=draft");
  const { content: published, loading } = usePublicContent();
  const { draft } = useDraftContent();
  const content = isDraftPreview ? draft : published;

  if (loading) return <PageSkeleton dark={dark} />;

  return (
    <HomePage
      content={content}
      dark={dark} setDark={setDark}
      lang={lang} setLang={setLang}
      t={t} isAdmin={isAdmin}
    />
  );
}

// ── AdminRoute ────────────────────────────────────────────────────────────────
function AdminRoute({ dark, setDark, isAdmin, login, logout }) {
  const { draft, updateDraft, publishDraft, discardDraft, resetContent, hasUnsaved } = useDraftContent();
  return (
    <AdminPage
      content={draft}
      updateContent={updateDraft}
      publishDraft={publishDraft}
      discardDraft={discardDraft}
      resetContent={resetContent}
      hasUnsaved={hasUnsaved}
      isAdmin={isAdmin}
      login={login}
      logout={logout}
      dark={dark}
      setDark={setDark}
    />
  );
}
